import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '../../contexts/ThemeContext';
import { useCalendarGlassStyles } from './calendarGlassStyles';

export function CalendarDayFilesPanel({
  selectedDateKey,
  onUploadFile,
  uploading = false,
  theme,
  children,
  compact = false,
  onError,
  onSuccess,
}) {
  const { isDarkMode } = useTheme();
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const glassStyles = useCalendarGlassStyles(theme, isDarkMode);
  const styles = useMemo(() => createStyles(theme, compact, glassStyles), [theme, compact, glassStyles]);

  const runImport = useCallback(async () => {
    if (!selectedDateKey) {
      onError?.('Select a day first.');
      return;
    }
    try {
      setImporting(true);
      setImportStatus('Opening file picker…');
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;
      setImportStatus('Uploading…');
      await onUploadFile?.(result.assets[0]);
      onSuccess?.('File attached to this day.');
    } catch (err) {
      console.error('day files import', err);
      onError?.(err?.message || 'Could not attach file.');
    } finally {
      setImporting(false);
      setImportStatus('');
    }
  }, [onError, onSuccess, onUploadFile, selectedDateKey]);

  const busy = uploading || importing;

  return (
    <View style={styles.wrap}>
      <View style={styles.toolbar}>
        <TouchableOpacity
          style={[styles.toolBtn, styles.toolBtnAccent, busy && styles.toolBtnDisabled]}
          onPress={() => void runImport()}
          disabled={busy}
        >
          {busy ? (
            <ActivityIndicator size="small" color={theme.brand.accent} />
          ) : (
            <>
              <Ionicons name="cloud-upload-outline" size={14} color={theme.brand.accent} />
              <Text style={[styles.toolBtnText, styles.toolBtnTextAccent]}>Upload file</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      {importStatus ? <Text style={styles.importStatus}>{importStatus}</Text> : null}

      <TouchableOpacity
        style={[styles.uploadBox, busy && styles.uploadBoxDisabled]}
        onPress={() => void runImport()}
        disabled={busy}
        activeOpacity={0.88}
        accessibilityLabel="Attach file to day"
      >
        <View style={[styles.uploadIconWrap, { borderColor: theme.ui.border, backgroundColor: theme.background.secondary }]}>
          <Ionicons name="attach-outline" size={22} color={theme.brand.primary} />
        </View>
        <Text style={[styles.uploadTitle, { color: theme.text.primary }]}>
          {busy ? 'Uploading…' : 'Attach file to this day'}
        </Text>
        <Text style={[styles.uploadSub, { color: theme.text.secondary }]}>
          PDF, images, spreadsheets, or any file type
        </Text>
      </TouchableOpacity>

      {children}
    </View>
  );
}

function createStyles(theme, compact, glassStyles) {
  return StyleSheet.create({
    wrap: { gap: compact ? 8 : 10, width: '100%' },
    toolbar: { flexDirection: 'row', gap: 8 },
    toolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      ...glassStyles.cardInset,
      paddingVertical: 8,
      paddingHorizontal: 12,
    },
    toolBtnAccent: { borderColor: `${theme.brand.accent}55` },
    toolBtnDisabled: { opacity: 0.6 },
    toolBtnText: { fontSize: 11, fontWeight: '800', color: theme.text.primary },
    toolBtnTextAccent: { color: theme.brand.accent },
    importStatus: { fontSize: 12, color: theme.brand.primary, fontWeight: '700' },
    uploadBox: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: compact ? 16 : 20,
      paddingHorizontal: 14,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    uploadBoxDisabled: { opacity: 0.7 },
    uploadIconWrap: {
      width: 48,
      height: 48,
      borderRadius: 24,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    uploadTitle: { fontSize: 13, fontWeight: '800', textAlign: 'center' },
    uploadSub: { fontSize: 11, lineHeight: 15, textAlign: 'center', maxWidth: 260 },
  });
}

export default CalendarDayFilesPanel;
